import React, { useMemo, useState } from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";
import themestyle from "../theme";

export type RevenueInsightPoint = {
  date: string;
  month: string;
  pass: number;
  passCount: number;
  classes: number;
  classesCount: number;
  event: number;
  eventCount: number;
};

export type RevenueInsightCounts = {
  pass: number;
  classes: number;
  event: number;
};

const REVENUE_SERIES = [
  { key: "pass", label: "Pass", color: "#B7D99A" },
  { key: "classes", label: "Class", color: "#2563EB" },
  { key: "event", label: "Event", color: "#F59E0B" },
] as const;

const CHART_HEIGHT = 240;
const CHART_WIDTH = 680;
const Y_AXIS_COLUMN_WIDTH = 64;
const MIN_POINT_GAP = 72;
const MIN_CHART_WIDTH = 240;
const CHART_PADDING = { top: 18, right: 40, bottom: 40, left: 44 };
function buildRevenueLinePoints(
  series: readonly RevenueInsightPoint[],
  key: (typeof REVENUE_SERIES)[number]["key"],
  maxValue: number,
  chartWidth: number,
) {
  const innerWidth = chartWidth - CHART_PADDING.left - CHART_PADDING.right;
  const innerHeight = CHART_HEIGHT - CHART_PADDING.top - CHART_PADDING.bottom;
  const safeMaxValue = maxValue || 1;

  return series.map((item, index) => {
    const x =
      CHART_PADDING.left +
      (series.length === 1 ? innerWidth / 2 : (index / (series.length - 1)) * innerWidth);
    const y = CHART_PADDING.top + innerHeight - (item[key] / safeMaxValue) * innerHeight;

    return { x, y, date: item.date };
  });
}

function buildRevenueLinePath(points: { x: number; y: number }[]) {
  return points.map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`).join(" ");
}

type RevenueInsightsChartProps = {
  series: RevenueInsightPoint[];
  counts?: RevenueInsightCounts;
  showCounts?: boolean;
  loading?: boolean;
  emptyMessage?: string;
  fullWidth?: boolean;
};

type RevenueSeriesKey = (typeof REVENUE_SERIES)[number]["key"];

function formatCurrency(value: number) {
  return `₹${value.toLocaleString("en-IN", {
    minimumFractionDigits: value % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatAxisValue(value: number) {
  return value.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function shouldRenderXAxisLabel(index: number, total: number, chartWidth: number) {
  const maxLabels = Math.max(2, Math.floor(chartWidth / 88));
  if (total <= maxLabels) return true;
  if (index === 0 || index === total - 1) return true;

  const stride = Math.ceil(total / maxLabels);
  return index % stride === 0;
}

function formatTitleCase(str: string) {
  if (!str) return "";
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export default function RevenueInsightsChart({
  series,
  counts,
  showCounts = false,
  loading = false,
  emptyMessage = "No revenue data found for this date range.",
  fullWidth = false,
}: RevenueInsightsChartProps) {
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  React.useLayoutEffect(() => {
    if (!containerRef.current) return;
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    const observer = new ResizeObserver(() => {
      updateWidth();
    });
    observer.observe(containerRef.current);
    updateWidth();
    return () => observer.disconnect();
  }, []);

  const [hoveredPoint, setHoveredPoint] = useState<{
    x: number;
    y: number;
    label: string;
    point: RevenueInsightPoint;
    key: RevenueSeriesKey;
    color: string;
  } | null>(null);
  const maxRevenueValue = Math.max(
    ...series.flatMap((item) => [item.pass, item.classes, item.event]),
    0,
  );
  const roundedMaxRevenueValue =
    maxRevenueValue <= 0 ? 4 : Math.ceil(maxRevenueValue / 4) * 4;
  const yAxisSteps = [0, 1, 2, 3, 4].map((step) => {
    const value = (roundedMaxRevenueValue / 4) * (4 - step);
    const y =
      CHART_PADDING.top +
      ((CHART_HEIGHT - CHART_PADDING.top - CHART_PADDING.bottom) / 4) * step;

    return {
      value,
      y,
    };
  });

  const chartWidth = useMemo(() => {
    const widthFromPoints =
      series.length <= 1
        ? CHART_WIDTH
        : CHART_PADDING.left + CHART_PADDING.right + (series.length - 1) * MIN_POINT_GAP;

    if (fullWidth && containerWidth) {
      const availableWidth = Math.max(containerWidth - Y_AXIS_COLUMN_WIDTH - 8, MIN_CHART_WIDTH);
      return availableWidth;
    }
    return Math.max(CHART_WIDTH, widthFromPoints);
  }, [series.length, fullWidth, containerWidth]);

  const revenueLineSeries = useMemo(
    () =>
      REVENUE_SERIES.map((item) => ({
        ...item,
        points: buildRevenueLinePoints(series, item.key, roundedMaxRevenueValue, chartWidth),
      })),
    [chartWidth, roundedMaxRevenueValue, series],
  );

  return (
    <>
      <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap", rowGap: 1 }}>
        {REVENUE_SERIES.map((item) => (
          <Stack key={item.key} direction="row" alignItems="center" spacing={0.75}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                bgcolor: item.color,
              }}
            />
            <Typography variant="caption" sx={{ fontWeight: 700, color: themestyle.colors.textSecondary }}>
              {item.label}
            </Typography>
            {showCounts && counts ? (
              <Chip
                size="small"
                label={counts[item.key]}
                sx={{
                  height: 22,
                  borderRadius: "999px",
                  bgcolor: "#F4F8EE",
                  color: "#355A1C",
                  fontWeight: 700,
                  border: "1px solid #DDEAC7",
                }}
              />
            ) : null}
          </Stack>
        ))}
      </Stack>

      <Box
        ref={containerRef}
        sx={{
          mt: 2,
          minHeight: { xs: 160, sm: CHART_HEIGHT },
          overflowX: "hidden",
          overflowY: "hidden",
          position: "relative",
        }}
        onMouseLeave={() => setHoveredPoint(null)}
      >
        {series.length === 0 ? (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography sx={{ color: themestyle.colors.textSecondary, fontWeight: 700 }}>
              {loading ? "Loading revenue insights..." : emptyMessage}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              width: "100%",
              display: "grid",
              gridTemplateColumns: `${Y_AXIS_COLUMN_WIDTH}px ${chartWidth}px`,
              columnGap: 0,
              alignItems: "stretch",
              pr: fullWidth ? 0 : 1,
            }}
          >
            <Box sx={{ position: "relative", height: CHART_HEIGHT }}>
              {yAxisSteps.map((step, index) => (
                <Typography
                  key={`y-axis-${index}`}
                  sx={{
                    position: "absolute",
                    right: 10,
                    top: step.y - 9,
                    width: Y_AXIS_COLUMN_WIDTH - 14,
                    textAlign: "right",
                    fontSize: 11,
                    fontWeight: 700,
                    lineHeight: "18px",
                    color: themestyle.colors.textSecondary,
                  }}
                >
                  {formatAxisValue(step.value)}
                </Typography>
              ))}
            </Box>

            <Box sx={{ position: "relative", height: CHART_HEIGHT }}>
              {yAxisSteps.map((step, index) => (
                <Box
                  key={index}
                  sx={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    top: step.y,
                    borderTop: "1px solid #D8DEE7",
                    pointerEvents: "none",
                    zIndex: 0,
                  }}
                />
              ))}
              <svg
                width={chartWidth}
                height={CHART_HEIGHT}
                viewBox={`0 0 ${chartWidth} ${CHART_HEIGHT}`}
                role="img"
                aria-label="Revenue insights line graph"
                style={{ position: "relative", zIndex: 1 }}
              >
                {revenueLineSeries.map((item) => (
                  <path
                    key={item.key}
                    d={buildRevenueLinePath(item.points)}
                    fill="none"
                    stroke={item.color}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                ))}

                {revenueLineSeries.map((item) =>
                  item.points.map((point) => {
                    const seriesPoint = series.find((entry) => entry.date === point.date);
                    if (!seriesPoint) return null;

                    return (
                      <circle
                        key={`${item.key}-${point.date}`}
                        cx={point.x}
                        cy={point.y}
                        r="5.5"
                        fill="#FFFFFF"
                        stroke={item.color}
                        strokeWidth="2"
                        style={{ cursor: "pointer" }}
                        onMouseEnter={() =>
                          setHoveredPoint({
                            x: point.x,
                            y: point.y,
                            label: item.label,
                            point: seriesPoint,
                            key: item.key,
                            color: item.color,
                          })
                        }
                      />
                    );
                  }),
                )}

                {series.map((item, index) => {
                  const x =
                    CHART_PADDING.left +
                    (series.length === 1
                      ? (chartWidth - CHART_PADDING.left - CHART_PADDING.right) / 2
                      : (index / (series.length - 1)) *
                        (chartWidth - CHART_PADDING.left - CHART_PADDING.right));
                  const isFirstLabel = index === 0;
                  const isLastLabel = index === series.length - 1;
                  const labelOffset = isFirstLabel ? 10 : isLastLabel ? -10 : 0;

                  if (!shouldRenderXAxisLabel(index, series.length, chartWidth)) {
                    return null;
                  }

                  return (
                    <text
                      key={item.date}
                      x={x + labelOffset}
                      y={CHART_HEIGHT - 10}
                      textAnchor={isFirstLabel ? "start" : isLastLabel ? "end" : "middle"}
                      fontSize="12"
                      fontWeight="700"
                      fill={themestyle.colors.textSecondary}
                    >
                      {formatTitleCase(item.month)}
                    </text>
                  );
                })}
              </svg>
              {hoveredPoint ? (
                <Box
                  sx={{
                    position: "absolute",
                    left: `clamp(12px, calc(${((hoveredPoint.x / chartWidth) * 100).toFixed(2)}% - 64px), calc(100% - 140px))`,
                    top: Math.max(8, hoveredPoint.y - 72),
                    minWidth: 128,
                    px: 1.25,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: "#111827",
                    color: "#FFFFFF",
                    boxShadow: "0px 12px 32px rgba(17, 24, 39, 0.24)",
                    pointerEvents: "none",
                    zIndex: 2,
                  }}
                >
                  <Stack spacing={0.25}>
                    <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#D1D5DB" }}>
                      {formatTitleCase(hoveredPoint.point.month)}
                    </Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 800, color: hoveredPoint.color }}>
                      {hoveredPoint.label}
                    </Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 800 }}>
                      {formatCurrency(hoveredPoint.point[hoveredPoint.key])}
                    </Typography>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#D1D5DB" }}>
                      Count: {hoveredPoint.point[`${hoveredPoint.key}Count` as const]}
                    </Typography>
                  </Stack>
                </Box>
              ) : null}
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
}
